import React from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Spinner from "../../components/spinner";
import useWalletSettings from "../../hooks/useWalletSettings";
import { onNumberKeydown, onNumberFieldPaste } from "../../utilities";

const WalletSettings = () => {
  const navigate = useNavigate();

  const {
    loading,
    saving,
    walletSettings,
    b2bConfig,
    b2cConfig,
    handleToggle,
    handleB2BConfigChange,
    handleB2CConfigChange,
    handleSave,
  } = useWalletSettings();

  if (loading) {
    return (
      <div className="container_xxl bg-[#eceff1] px-4">
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container_xxl bg-[#eceff1] px-4">
      <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-10">
        <div>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </div>
        <div>
          <h1 className="heading">Wallet Settings</h1>
        </div>
      </div>

      <div className="py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    Wallet Configuration
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Manage wallet functionality for different user types
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Current Status */}
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Current Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        walletSettings.enableB2B
                          ? "bg-emerald-500"
                          : "bg-gray-300"
                      }`}
                    ></div>
                    <span className="text-sm font-medium text-gray-700">
                      B2B Wallet:{" "}
                      {walletSettings.enableB2B ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        walletSettings.enableB2C
                          ? "bg-purple-500"
                          : "bg-gray-300"
                      }`}
                    ></div>
                    <span className="text-sm font-medium text-gray-700">
                      B2C Wallet:{" "}
                      {walletSettings.enableB2C ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
              </div>
              {/* B2B Settings */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-emerald-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-emerald-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800 mb-1">
                        Business to Business (B2B)
                      </h3>
                      <p className="text-emerald-700 text-sm leading-relaxed">
                        Enable wallet functionality for business customers. This
                        allows business users to use the coins in their wallet
                        for bulk purchases and corporate transactions.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={walletSettings.enableB2B}
                          onChange={() => handleToggle("enableB2B")}
                          color="success"
                          size="medium"
                        />
                      }
                      label=""
                      className="m-0"
                    />
                  </div>
                </div>
              </div>

              {/* B2B Configuration Form */}
              {walletSettings.enableB2B && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-300">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    B2B Wallet Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Threshold Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={b2bConfig.threshold}
                        onChange={(e) =>
                          handleB2BConfigChange("threshold", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter threshold amount"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Minimum wallet balance required
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Order Value (₹)
                      </label>
                      <input
                        type="number"
                        value={b2bConfig.min_order_value}
                        onChange={(e) =>
                          handleB2BConfigChange(
                            "min_order_value",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter minimum order value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Minimum order amount for wallet use
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum Order Value (₹)
                      </label>
                      <input
                        type="number"
                        value={b2bConfig.max_order_value}
                        onChange={(e) =>
                          handleB2BConfigChange(
                            "max_order_value",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter maximum order value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Maximum order amount for wallet use
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Redeem Value (%)
                      </label>
                      <input
                        type="number"
                        value={b2bConfig.redeem_value}
                        onChange={(e) =>
                          handleB2BConfigChange("redeem_value", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter redeem value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* B2C Settings */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <svg
                        className="w-6 h-6 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-purple-800 mb-1">
                        Business to Consumer (B2C)
                      </h3>
                      <p className="text-purple-700 text-sm leading-relaxed">
                        Enable wallet functionality for individual customers.
                        This allows regular customers to use the coins in their
                        wallet for faster checkout.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={walletSettings.enableB2C}
                          onChange={() => handleToggle("enableB2C")}
                          color="secondary"
                          size="medium"
                        />
                      }
                      label=""
                      className="m-0"
                    />
                  </div>
                </div>
              </div>

              {/* B2C Configuration Form */}
              {walletSettings.enableB2C && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-300">
                  <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    B2C Wallet Configuration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Threshold Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={b2cConfig.threshold}
                        onChange={(e) =>
                          handleB2CConfigChange("threshold", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter threshold amount"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Minimum wallet balance required
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Order Value (₹)
                      </label>
                      <input
                        type="number"
                        value={b2cConfig.min_order_value}
                        onChange={(e) =>
                          handleB2CConfigChange(
                            "min_order_value",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter minimum order value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Minimum order amount for wallet use
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum Order Value (₹)
                      </label>
                      <input
                        type="number"
                        value={b2cConfig.max_order_value}
                        onChange={(e) =>
                          handleB2CConfigChange(
                            "max_order_value",
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter maximum order value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Maximum order amount for wallet use
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Redeem Value (%)
                      </label>
                      <input
                        type="number"
                        value={b2cConfig.redeem_value}
                        onChange={(e) =>
                          handleB2CConfigChange("redeem_value", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                        placeholder="Enter redeem value"
                        onKeyDown={(e) => onNumberKeydown(e, { isPositiveNumber: true })}
                        onPaste={(e) => onNumberFieldPaste(e, { isPositiveNumber: true })}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletSettings;
